/* ------------------------------------------------------------------------------------------------------------------
   PRODUCT CARD SIZES — the sizes sheet a card opens on touch
   ------------------------------------------------------------------------------------------------------------------

   Tapping the "+" on a card used to open quick-buy-modal, which downloads the whole product page to lift the
   quick-buy markup out of it — around half a megabyte, and the customer stares at a locked page until it lands.

   Nothing in that response is needed to pick a size: the card already renders every variant, each in its own
   <product-form> with its variant id (the size selector shown on hover, which touch devices never see). So this
   opens a sheet built from the card's own markup — instantly, offline — and adds by submitting the very form that
   belongs to the tapped size. The add path is therefore identical to the desktop one: the theme's product-form
   answers the submit, dispatches variant:add, and the Shop the Look drawer confirms.

   Markup comes from snippets/product-card-sizes-sheet.liquid, rendered once per page.
   ------------------------------------------------------------------------------------------------------------------ */

if (!customElements.get('product-card-sizes')) {
    class ProductCardSizes extends HTMLElement {
        static SELECTORS = {
            trigger: '[data-card-sizes-trigger]',
            card: 'product-card',
            source: '.product-card__size-selector',
            sourceVariant: 'product-form',
            sourceIdInput: 'input[name="id"]',
            sourceSubmit: 'button[type="submit"]',
            sourceForm: 'form',
            sheetTemplate: '#product-card-sizes-template',
            sizeTemplate: '#product-card-size-template',
            overlay: '[data-card-sizes-overlay]',
            list: '[data-card-sizes-list]',
            size: '[data-card-size]',
            sizeLabel: '[data-card-size-label]',
            statusLowStock: '[data-card-size-status-low]',
            statusSoldOut: '[data-card-size-status-sold-out]',
            kleepCta: '.kleep-secondary-button',
            alertPopover: '#product-create-alert-popup'
        };

        static CLASSES = {
            hidden: 'hidden-phone',
            lowStock: 'block-swatch__item--low-stock',
            outOfStock: 'block-swatch__item--out-of-stock'
        };

        static ATTRIBUTES = { lowStock: 'data-low-stock' };

        static EVENTS = { variantAdd: 'variant:add', cartError: 'cart:error' };

        static ESCAPE_KEY = 'Escape';

        // Safety net for _closeAfterAdd: if the add never settles, the sheet still closes.
        static ADD_TIMEOUT_MS = 6000;

        connectedCallback() {
            this._abort = new AbortController();
            this._card = this.closest(ProductCardSizes.SELECTORS.card);
            this._source = this._card?.querySelector(ProductCardSizes.SELECTORS.source);
            this._trigger = this.querySelector(ProductCardSizes.SELECTORS.trigger);

            // Without the card's size selector there is nothing to build a sheet from. Liquid only
            // renders this element alongside it, so this is a guard, not a fallback.
            if (!this._source) {
                return;
            }

            this._trigger?.addEventListener('click', () => this.open(), { signal: this._abort.signal });
        }

        disconnectedCallback() {
            this._abort?.abort();
            this._openAbort?.abort();
            // The sheet is a child of the body, so it outlives this element unless dropped here
            this._sheet?.remove();
            this._sheet = null;
        }

        open() {
            this._sheet ??= this._buildSheet();

            if (!this._sheet) {
                return;
            }

            // On the body, not in the card: the card sits in a transformed, clipped grid, which a
            // fixed-position sheet cannot escape.
            document.body.append(this._sheet);
            this._sheet.classList.remove(ProductCardSizes.CLASSES.hidden);

            // Its own controller, so the listener is dropped on close rather than living as long
            // as the card does.
            this._openAbort = new AbortController();
            document.addEventListener('keydown', (event) => {
                if (event.key === ProductCardSizes.ESCAPE_KEY) {
                    this.close();
                }
            }, { signal: this._openAbort.signal });

            this._sheet.querySelector(ProductCardSizes.SELECTORS.size)?.focus({ preventScroll: true });
        }

        close() {
            // Idempotent: _closeAfterAdd's timeout can land long after the customer closed the
            // sheet by hand, and must not pull focus back at that point.
            if (!this._sheet?.isConnected) {
                return;
            }

            this._openAbort?.abort();
            this._sheet.classList.add(ProductCardSizes.CLASSES.hidden);
            this._sheet.remove();
            // Back where it came from, so the sheet does not strand keyboard focus on a removed node
            this._trigger?.focus({ preventScroll: true });
        }

        // The variants as the card already describes them: one <product-form> each, holding the
        // variant id in its hidden input and the size on its submit button.
        get _variants() {
            return Array.from(this._source.querySelectorAll(ProductCardSizes.SELECTORS.sourceVariant))
                .map((holder) => ({
                    form: holder.querySelector(ProductCardSizes.SELECTORS.sourceForm),
                    id: holder.querySelector(ProductCardSizes.SELECTORS.sourceIdInput)?.value,
                    label: holder.querySelector(ProductCardSizes.SELECTORS.sourceSubmit)?.textContent.trim(),
                    soldOut: holder.querySelector(ProductCardSizes.SELECTORS.sourceSubmit)?.disabled === true,
                    lowStock: holder.hasAttribute(ProductCardSizes.ATTRIBUTES.lowStock)
                }))
                .filter((variant) => variant.form && variant.id && variant.label);
        }

        _buildSheet() {
            const template = document.querySelector(ProductCardSizes.SELECTORS.sheetTemplate);
            const sheet = template?.content.firstElementChild.cloneNode(true);

            if (!sheet) {
                return null;
            }

            const { signal } = this._abort;
            sheet.querySelector(ProductCardSizes.SELECTORS.overlay)
                ?.addEventListener('click', () => this.close(), { signal });

            // The Kleep widget is opened by the product on the button — see component-kleep-cta.js
            const kleepCta = sheet.querySelector(ProductCardSizes.SELECTORS.kleepCta);
            if (kleepCta) {
                kleepCta.dataset.productId = this._source.dataset.productId ?? '';
            }

            const list = sheet.querySelector(ProductCardSizes.SELECTORS.list);
            list?.append(...this._variants.map((variant) => this._buildSize(variant)).filter(Boolean));

            return sheet;
        }

        _buildSize(variant) {
            const template = document.querySelector(ProductCardSizes.SELECTORS.sizeTemplate);
            const button = template?.content.firstElementChild.cloneNode(true);

            if (!button) {
                return null;
            }

            button.querySelector(ProductCardSizes.SELECTORS.sizeLabel).textContent = variant.label;
            // Named the same way option-value.liquid names it on the product page, because the
            // back-in-stock popover reads the variant off whichever element opened it.
            button.dataset.id = variant.id;
            button.classList.toggle(ProductCardSizes.CLASSES.outOfStock, variant.soldOut);
            button.classList.toggle(ProductCardSizes.CLASSES.lowStock, variant.lowStock && !variant.soldOut);

            // Only the status line that applies survives. Removed rather than hidden, because
            // .block-swatch__item-status sets its own display and would beat the hidden attribute.
            let status = null;
            if (variant.soldOut) {
                status = ProductCardSizes.SELECTORS.statusSoldOut;
            } else if (variant.lowStock) {
                status = ProductCardSizes.SELECTORS.statusLowStock;
            }

            [ProductCardSizes.SELECTORS.statusSoldOut, ProductCardSizes.SELECTORS.statusLowStock]
                .filter((selector) => selector !== status)
                .forEach((selector) => button.querySelector(selector)?.remove());

            button.addEventListener('click', () => this._selectVariant(variant, button), { signal: this._abort.signal });

            return button;
        }

        _selectVariant(variant, button) {
            if (variant.soldOut) {
                this._openBackInStock(button);
                return;
            }

            this._addVariant(variant);
        }

        // Submits the card's own form for that size, so the theme's product-form does the add and
        // fires the events the confirmation drawer listens for.
        _addVariant(variant) {
            variant.form.requestSubmit();
            this._closeAfterAdd(variant.form);
        }

        // Left open until the add lands, so the sheet does not blink out before the drawer arrives.
        _closeAfterAdd(form) {
            let closed = false;
            const finish = () => {
                if (closed) {
                    return;
                }

                closed = true;
                this.close();
            };

            form.addEventListener(ProductCardSizes.EVENTS.variantAdd, finish, { once: true });
            form.addEventListener(ProductCardSizes.EVENTS.cartError, finish, { once: true });
            setTimeout(finish, ProductCardSizes.ADD_TIMEOUT_MS);
        }

        // Sold-out sizes offer the back-in-stock signup instead, as they do on a product page. The
        // popover is shared by the whole page and takes the variant from its activator, so the
        // button has to be handed over — see component-klaviyo-backinstock.js.
        _openBackInStock(activator) {
            document.querySelector(ProductCardSizes.SELECTORS.alertPopover)?.show({ activator });
        }
    }

    customElements.define('product-card-sizes', ProductCardSizes);
}
